import { Component, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { DynamicDialogConfig, DynamicDialogModule, DynamicDialogRef } from 'primeng/dynamicdialog';

export interface BulkConfirmData {
  /** Question shown above the list, e.g. "Fire these 3 candidates?" */
  heading: string;
  /** Candidate names listed for review before confirming. */
  names: string[];
  /** Label above the optional reason textarea; omit to hide the textarea. */
  reasonLabel?: string;
  reasonPlaceholder?: string;
  confirmLabel: string;
  /** PrimeNG button severity for the confirm button; "danger" for fire and deny. */
  confirmSeverity?: 'danger' | 'success' | 'primary';
}

export interface BulkConfirmResult {
  reason: string;
}

/**
 * MP-5694 (items 1 & 3): confirmation before a bulk fire / deny / approve, listing the
 * selected candidate names. Closes with `{ reason }` on confirm, or nothing on cancel.
 */
@Component({
  selector: 'app-bulk-confirm',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, InputTextareaModule, DynamicDialogModule],
  templateUrl: './bulk-confirm.component.html',
  styleUrl: './bulk-confirm.component.scss'
})
export class BulkConfirmComponent {
  private readonly data = signal<BulkConfirmData>({ heading: '', names: [], confirmLabel: 'Confirm' });

  readonly heading = computed(() => this.data().heading);
  readonly names = computed(() => this.data().names ?? []);
  readonly count = computed(() => this.names().length);
  readonly reasonLabel = computed(() => this.data().reasonLabel ?? '');
  readonly reasonPlaceholder = computed(() => this.data().reasonPlaceholder ?? '');
  readonly confirmLabel = computed(() => this.data().confirmLabel);
  readonly confirmSeverity = computed(() => this.data().confirmSeverity ?? 'primary');

  readonly reason = signal('');

  constructor(private ref: DynamicDialogRef, config: DynamicDialogConfig<BulkConfirmData>) {
    if (config.data) {
      this.data.set(config.data);
    }
  }

  cancel(): void {
    this.ref.close();
  }

  confirm(): void {
    const result: BulkConfirmResult = { reason: this.reason().trim() };
    this.ref.close(result);
  }
}
